import NotFound from "@/components/AppRouter/NotFound";
import Container from "@/components/ContentContainer/Container";
import { useCreateCompany } from "@/hooks/useAdmin";
import useTranslation from "@/hooks/useTranslation";
import { useUserSettings } from "@/hooks/useUserSettings";
import { ALL_EF_IMPACTS } from "@/pages/dashboard/utils/EFMetrics";
import { ALL_RECIPE_IMPACTS } from "@/pages/dashboard/utils/recipeMetrics";
import { PREMIUM_IMPACT_METRIC_KEYS } from "@/pages/dashboard/utils/types";
import { Button, Checkbox, Col, Form, Input, message, Row, Select, Space } from "antd";
import { useForm } from "antd/es/form/Form";
import { useNavigate } from "react-router-dom";

export const COMPANY_TYPES = [
  { value: "production", label: "Production" },
  { value: "test", label: "Test" },
  { value: "trial", label: "Trial" },
];

export const AccessFormItem = (
  <Form.Item
    required={true}
    name="access"
    valuePropName="value"
  >
    <Checkbox.Group style={{ width: "100%" }}>
      <Row gutter={[20, 20]}>
        <Col>
          <Space direction={"vertical"}>
            <p style={{ marginBottom: 0 }}>DASHBOARD</p>
            <Checkbox value="lca">LCA</Checkbox>
            <Checkbox value="ghg">GHG</Checkbox>
            <Checkbox value="products">Products</Checkbox>
          </Space>
        </Col>
        <Col>
          <Space direction={"vertical"}>
            <p style={{ marginBottom: 0 }}>ONBOARDING</p>
            <Checkbox value="supplier_facility_invite">Facility supplier invite</Checkbox>
            <Checkbox value="product_scenario_builder">Product scenario builder</Checkbox>
          </Space>
        </Col>
        <Col>
          <Space direction={"vertical"}>
            <p style={{ marginBottom: 0 }}>MATCHING</p>
            <Checkbox value="epd">EPD</Checkbox>
            <Checkbox value="custom_impact_references">Custom impact references</Checkbox>
          </Space>
        </Col>
        <Col>
          <Space direction={"vertical"}>
            <p style={{ marginBottom: 0 }}>RECIPE METRICS</p>
            {Object.values(ALL_RECIPE_IMPACTS)
              .filter((metric) => PREMIUM_IMPACT_METRIC_KEYS.includes(metric.key))
              .map((metric) => (
                <Checkbox
                  key={metric.key}
                  value={"metric:" + metric.key}
                >
                  {metric.label}
                </Checkbox>
              ))}
          </Space>
        </Col>
        <Col>
          <Space direction={"vertical"}>
            <p style={{ marginBottom: 0 }}>EF METRICS</p>
            {Object.values(ALL_EF_IMPACTS)
              .filter((metric) => PREMIUM_IMPACT_METRIC_KEYS.includes(metric.key))
              .map((metric) => (
                <Checkbox
                  key={metric.key}
                  value={"metric:" + metric.key}
                >
                  {metric.label}
                </Checkbox>
              ))}
          </Space>
        </Col>
      </Row>
    </Checkbox.Group>
  </Form.Item>
);

export default function NewCompany() {
  const [form] = useForm();
  const { data: userSettings } = useUserSettings();
  const companyCreate = useCreateCompany();
  const navigate = useNavigate();
  const t = useTranslation();

  if (userSettings && userSettings.role !== "root_admin") {
    return <NotFound />;
  }

  const onSubmit = () => {
    form.validateFields().then((values) => {
      companyCreate
        .mutateAsync({
          company: values,
        })
        .then((result) => {
          if (result.status === 200) {
            message.success("Company saved succesfully.");
            navigate("/admin/companies");
          }
        })
        .catch(() => {
          message.error(t.errors.general);
        });
    });
  };

  return (
    <Container title={"Create new company"}>
      <Form
        form={form}
        labelCol={{
          span: 8,
        }}
        wrapperCol={{
          span: 16,
        }}
        style={{
          maxWidth: 950,
        }}
        initialValues={{ access: ["lca", "ghg", "products"] }}
        autoComplete="off"
        layout={"vertical"}
      >
        <Form.Item
          required={true}
          label="Name"
          name="name"
        >
          <Input />
        </Form.Item>
        <Form.Item
          required={true}
          label="Type"
          name="type"
        >
          <Select options={COMPANY_TYPES} />
        </Form.Item>
        {AccessFormItem}
        <Button
          disabled={companyCreate.isLoading}
          loading={companyCreate.isLoading}
          onClick={onSubmit}
          type="primary"
        >
          Save
        </Button>
      </Form>
    </Container>
  );
}
